/**
 * Translate an axios error into a human-friendly message for toast
 * display. Prefers the backend's `error.response.data.message` when
 * present (controllers throw `HttpException` subclasses whose
 * `message` field is already user-facing). Falls back to status-code
 * templates, then to the raw axios message as a last resort.
 */
const AUTH_PHRASES = new Set([
    "Unauthorized User",
    "Unauthorized User.",
    "Authentication failed: Invalid token.",
    "Not authorized, token failed",
]);

export const friendlyError = (error) => {
    const status = error?.response?.status;
    const bodyMessage = error?.response?.data?.message;

    // Backend sometimes returns 500 for what is really 401/403 (see
    // users.controller.ts). Normalise those phrases up-front so the toast
    // reads as an access-denied message, not as server-side breakage.
    if (typeof bodyMessage === "string" && AUTH_PHRASES.has(bodyMessage.trim())) {
        return "You do not have access to this resource.";
    }

    if (bodyMessage && typeof bodyMessage === "string") return bodyMessage;

    if (status === 401) return "Your session has expired. Please sign in again.";
    if (status === 403) return "You do not have access to this resource.";
    if (status === 404) return "We couldn't find what you're looking for.";
    if (status === 409) return "That request conflicts with something else on the server.";
    if (status === 413) return "The file you tried to upload is too large.";
    if (status === 429) return "Too many requests — please slow down and try again.";
    if (typeof status === "number" && status >= 500) {
        return "Something went wrong on our side. Please try again shortly.";
    }
    if (error?.message === "Network Error") {
        return "Couldn't reach the server. Check your connection and try again.";
    }
    return error?.message || "Something went wrong.";
};

export default friendlyError;
